import React, { useEffect, useState } from 'react';
import './ServiceRequestList.scss';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, Dialog, Drawer, makeStyles,TextField
} from '@material-ui/core';
import { http, FxDateDisplay, FxSelect, formatService, AlertService, localstorage } from '../../fx-core';
import { NoRecords } from '../../NoRecords/NoRecords';
import Typography from '@material-ui/core/Typography';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';
import ServiceRequestDetail from './ServiceRequestDetail/ServiceRequestDetail';
import SaleServiceRequestDetail from './SaleServiceRequestDetail/SaleServiceRequestDetail';

const styles = makeStyles((theme) => ({
  postsDrawer: {
    paddingTop: "70px",
    [theme.breakpoints.down(599)]: { width: '60%' },
    [theme.breakpoints.up(600)]: { width: '40%' },
    [theme.breakpoints.up(1025)]: { width: '20%' }
  },
  drawerWidth: {
    [theme.breakpoints.down(599)]: { width: '90%' },
    [theme.breakpoints.up(600)]: { width: '70%' },
    [theme.breakpoints.up(1025)]: { width: '40%' }
  }
}));

const ServiceRequestList = () => {
  const classes = styles();

  let defaultContext: any = {
    communityId: 0,
    serRequestStatusId: -1,
    serviceId: -1,
    communityCode: "" // will store community code for API call
  };

  const [currentContext, setCurrentContext] = useState(defaultContext);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [communityList, setCommunityList] = useState([]);
  const [serRequestStatus, setSerRequestStatus] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedReq, setSelectedReq] = useState<any>({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [saleDetailOpen, setSaleDetailOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectMessage, setRejectMessage] = useState<string>('');


  /**
   * Handle dropdown changes for Community, Status, Service
   */
  const handleInputChange = (item: any) => {
    console.log("Item changed: ", item);

    handleItemChange({ [item.name]: item.value });

    // Update service requests based on selected filters
    if (item.name == "communityId") {
      getServiceRequests({ communityId: item.value, serRequestStatusId: currentContext.serRequestStatusId, serviceId: currentContext.serviceId });
      // store community code (id) for API call later
      handleItemChange({ communityCode: item.value });
    }
    else if (item.name == "serRequestStatusId") {
      getServiceRequests({ serRequestStatusId: item.value, communityId: currentContext.communityId, serviceId: currentContext.serviceId });
    }
    else if (item.name == "serviceId") {
      getServiceRequests({ serviceId: item.value, communityId: currentContext.communityId, serRequestStatusId: currentContext.serRequestStatusId });
    }
  };

  const handleItemChange = (data: any) => {
    setCurrentContext(prevState => {
      return { ...prevState, ...data }
    });
  };

  /**
   * Fetch service requests list
   */
  const getServiceRequests = (data: any) => {
    let inputData = {
      communityId: data.communityId,
      serviceRequestStatusId: data.serRequestStatusId,
      serviceId: data.serviceId,
      currentDate: formatService.getCurrentDateForServer()
    };
    let apiOptions: any = {
      url: `servicerequest/query`,
      data: inputData
    };
    http.post(apiOptions).then(res => {
      let result = res.data;
      setServiceRequests(result);
    })
  };

  /**
   * Fetch communities for current user role
   */
  const getUserRoleCommunities = () => {
    let apiOptions: any = {
      url: 'community/role/list',
      data: {}
    };
    http.post(apiOptions).then(res => { getUserRoleCommunitiesCallBack(res) });
  };

  const getUserRoleCommunitiesCallBack = (res: any) => {
    setCommunityList(res.data);
    if (res.data.length > 0) {
      const commuId = res.data[0].id;
      handleItemChange({ communityId: commuId, communityCode: commuId });
      getServiceRequests({ communityId: commuId });
    }
  };

  /**
   * Lookup for request services and status
   */
  const initLookup = () => {
    let inputData = {
      lookups: {
        RequestServices: { default: true },
        ServiceRequestStatus: { default: true }
      },
      filters: {}
    };
    let apiOptions = {
      url: 'options/lookup',
      data: inputData
    };
    http.post(apiOptions).then(response => {
      let result = response.data;
      setServices(result.RequestServices);
      setSerRequestStatus(result.ServiceRequestStatus);
    })
  }

  /**
   * Send notification on approve/reject
   */
  const sendServiceRequestNotification = (
    notificationType: 'request_approved' | 'request_rejected',
    row: any
  ) => {
    try {
      http.post({
        url: 'specialevent/notify',
        data: {
          notificationType: notificationType,
          serviceRequestId: row.id,
          stallId: row.stallId,
          stallName: row.stallName,
          communityId: row.communityId,
          communityName: row.communityName,
          createdBy: localstorage.getItem('userId')
        }
      });
    } catch (e) {
      console.error('Service request notification failed', e);
    }
  };

  /**
   * Approve handler
   * Fetches community entryFee, updates status, then notifies merchant
   */
  const confirmApprove = (item: any) => {
    setSelectedReq(item);

    // fetch community details API
    let apiOptions: any = {
      url: `community/${currentContext.communityCode}`, // use selected communityId/code
      data: {}
    };

    http.get(apiOptions).then(res => {
      let community = res.data;
      let amount = community.entryFee; // take entryFee
      let updatedReq = {
        ...item,
        amount: amount,
        comments: "yes", // default comment
        serviceRequestStatusId: 2 // approved
      };
      updateServiceReqStatus(updatedReq);
      sendServiceRequestNotification('request_approved', item); // 🔔 notify merchant
    }).catch(err => {
      console.error("Error fetching community:", err);
      AlertService.showMsg({ message: "Failed to fetch community details" });
    });
  };

  /**
   * Reject handler — opens dialog to collect rejection reason
   */
  const confirmReject = (item: any) => {
    setSelectedReq(item);
    setRejectComment(""); // Reset comment before opening

    // Set dynamic message
    const msg = `Do you want to cancel the service request #${item.serviceRequestIdentifier}?`;
    setRejectMessage(msg);

    setRejectDialogOpen(true);
  };

  const handleRejectClose = () => {
    setRejectDialogOpen(false);
    setRejectComment("");
    setRejectMessage('');
  };

  const handleRejectConfirm = () => {
    if (!rejectComment.trim()) {
      AlertService.showMsg({ message: "Please enter a rejection reason." });
      return;
    }

    let updatedReq = {
      ...selectedReq,
      comments: rejectComment.trim(),
      serviceRequestStatusId: 3 // rejected
    };
    updateServiceReqStatus(updatedReq);
    sendServiceRequestNotification('request_rejected', selectedReq); // 🔔 notify merchant
    handleRejectClose();
  };

  /**
   * API to update service request status
   */
  const updateServiceReqStatus = (item: any) => {
    let apiOptions = {
      url: 'servicerequest/updatestatus',
      data: item
    };
    http.put(apiOptions).then(res => {
      AlertService.showMsg({ message: "Service request status updated successfully" });
      getServiceRequests(currentContext);
    });
  }

  useEffect(() => {
    initLookup();
    getUserRoleCommunities();
  }, []);

  const openDetail = () => setDetailOpen(true);
  const closeDetail = () => { setDetailOpen(false); getServiceRequests(currentContext); };
  const openSaleDetail = () => setSaleDetailOpen(true);
  const closeSaleDetail = () => { setSaleDetailOpen(false); getServiceRequests(currentContext); };

  const viewDetail = (item: any) => {
    setSelectedReq(item);
    if (item.saleId > 0) openSaleDetail(); else openDetail();
  }

  return (
    <div className='pending-payment-list-ui pt-5'>
      <div className="title-font custom-head pl-4 pt-5">
        <Typography color="primary" className="breadcrumbs-style">Service Request List</Typography>
      </div>
      <div className='row my-4 mx-2'>
        <div className="col-3 pt-3">
          <FxSelect name="communityId" variant="outlined" label="Communities"
            options={communityList}
            selectedValue={currentContext.communityId}
            valueField="id"
            displayField="name"
            size="small"
            fullWidth
            onValueChange={handleInputChange} />
        </div>
        <div className="col-3 pt-3">
          <FxSelect name="serRequestStatusId" variant="outlined" label="Status"
            options={serRequestStatus}
            selectedValue={currentContext.serRequestStatusId}
            valueField="id"
            displayField="text"
            size="small"
            fullWidth
            onValueChange={handleInputChange} />
        </div>
        <div className="col-3 pt-3">
          <FxSelect name="serviceId" variant="outlined" label="Services"
            options={services}
            selectedValue={currentContext.serviceId}
            valueField="id"
            displayField="text"
            size="small"
            fullWidth
            onValueChange={handleInputChange} />
        </div>
      </div>

      <div className='my-4 mx-4 pt-2'>
        <div className='my-1 d-flex justify-content-end'>
          <span className='pr-1 color-grey'><i className="fas fa-info-circle"></i></span>
          <span className='color-grey font-13'>Please verify pending payments before approval.</span>
        </div>
        <TableContainer component={Paper}>
          <Table className="table-striped" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Request Identifier</TableCell>
                <TableCell>Request Type</TableCell>
                <TableCell>Request Date</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Stall</TableCell>
                <TableCell>Service</TableCell>
                <TableCell align='center'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {serviceRequests.length == 0 &&
                <TableCell colSpan={7}><NoRecords /></TableCell>
              }
              {serviceRequests.length > 0 && serviceRequests.map((row: any, i) => (
                <TableRow key={i}>
                  <TableCell># {row.serviceRequestIdentifier}</TableCell>
                  <TableCell>{row.serviceRequestType}</TableCell>
                  <TableCell><FxDateDisplay value={row.requestDate} /></TableCell>
                  <TableCell>{row.requestedByName}</TableCell>
                  <TableCell>{row.serviceRequestStatus}</TableCell>
                  <TableCell>{row.stallName}</TableCell>
                  <TableCell>{row.serviceName}</TableCell>
                  <TableCell>
                    {row.serviceRequestStatusId == 1 &&
                      <div>
                        <Button variant='outlined' size='small' className='button-style'
                          onClick={() => confirmApprove(row)}>Approve</Button>
                        <Button variant='outlined' size='small' className='button-style ml-2'
                          onClick={() => confirmReject(row)}>Reject</Button>
                      </div>
                    }
                    <Button variant='outlined' size='small' className='button-style mt-1'
                      onClick={() => viewDetail(row)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <Dialog
        open={rejectDialogOpen}
        maxWidth="sm"
        fullWidth
        onClose={handleRejectClose}
        aria-labelledby="reject-dialog-title"
        aria-describedby="reject-dialog-description"
      >
        <ConfirmDialog
          message={
            <>
              <div style={{ marginBottom: '15px' }}>
                {rejectMessage}
              </div>
              <TextField
                autoFocus
                margin="dense"
                id="rejectionComment"
                label="Enter Rejection Reason"
                placeholder="Required reason for rejection"
                type="text"
                fullWidth
                multiline
                rows={3}
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                variant="outlined"
              />
            </>
          }
          handleDialogClose={handleRejectClose}
          onConfirmCallback={handleRejectConfirm}
          confirmText="YES"
          cancelText="NO"
        />
      </Dialog>

      <Drawer anchor={"right"} open={detailOpen} onClose={closeDetail} classes={{ paper: classes.drawerWidth }}>
        <ServiceRequestDetail serviceRequestId={selectedReq.id} handleClose={closeDetail} />
      </Drawer>

      <Drawer anchor={"right"} open={saleDetailOpen} onClose={closeSaleDetail} classes={{ paper: classes.drawerWidth }}>
        <SaleServiceRequestDetail serviceRequestId={selectedReq.id} handleClose={closeSaleDetail} />
      </Drawer>
    </div>
  );
}

export default ServiceRequestList;